/**
 * PROGRAMAS académicos de la org activa (F2-A): carrera | diplomado | curso
 * libre. Listado + creación (owner/org_admin vía write-path) y detalle con la
 * lista ordenada de cursos del programa.
 */
import { type Component, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { GraduationCap, Plus, Trash2 } from "lucide-solid";
import {
  addProgramCourseMutation,
  asProgramKind,
  createProgramMutation,
  DEFAULT_PROGRAM_KIND,
  type ProgramKind,
  removeProgramCourseMutation,
  updateProgramMutation,
} from "@space/shared";
import { Link, useNavigate } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { NotFoundState } from "@/components/ui/not-found-state";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { listEntranceProps, useListEntrance } from "@/lib/animation/list-entrance";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useActiveOrg } from "../hooks/use-active-org";
import {
  useOrgCourses,
  useProgram,
  useProgramCourses,
  useProgramsByOrg,
} from "../hooks/use-cohort-data";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

// El vocabulario vive en @space/shared (espejo del CHECK de la migración): la
// copia local se desincronizó al añadir `workshop` en B5, que es exactamente el
// fallo que un solo sitio evita.

export const ProgramsView: Component = () => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { activeOrgId, isLoading } = useActiveOrg();

  const programs = useProgramsByOrg(activeOrgId);
  const isEntering = useListEntrance({
    scope: () => `programs:${activeOrgId() ?? ""}`,
    hasItems: () => (programs.data ?? []).length > 0,
  });
  const [name, setName] = createSignal("");
  const [kind, setKind] = createSignal<ProgramKind>(DEFAULT_PROGRAM_KIND);

  const create = async () => {
    const db = drizzle();
    const org = activeOrgId();
    if (!db || !org) {
      return;
    }
    await createProgramMutation(db, {
      organizationId: org,
      name: name(),
      kind: kind(),
    });
    setName("");
    showToast(t(msg`Program created`), "success");
  };

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <h1 class="font-display text-2xl font-extrabold tracking-tight" data-testid="programs-title">
        {t(msg`Programs`)}
      </h1>
      <div class="flex gap-2">
        <input
          class={field}
          data-testid="program-name"
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          placeholder={t(msg`Program name`)}
        />
        <select
          class={`${field} max-w-40`}
          data-testid="program-kind"
          value={kind()}
          onChange={(e) => setKind(asProgramKind(e.currentTarget.value))}
        >
          <option value="career">{t(msg`Career`)}</option>
          <option value="diploma">{t(msg`Diploma`)}</option>
          {/* B5 · N2: el taller de 1–2 sesiones sueltas, el formato del gancho. */}
          <option value="workshop">{t(msg`Workshop (1-2 sessions)`)}</option>
          <option value="free_course">{t(msg`Free course`)}</option>
        </select>
        <Button data-testid="program-create" onClick={() => void create()} class="gap-1">
          <Plus class="h-4 w-4" /> {t(msg`Add`)}
        </Button>
      </div>
      <Show when={!isLoading()} fallback={<Skeleton class="h-32 w-full rounded-2xl" />}>
        <ul class="space-y-2" data-testid="programs-list">
          <For each={programs.data ?? []}>
            {(p, index) => {
              const entrance = () => listEntranceProps(isEntering(), index());
              return (
                <li class={entrance().class} style={entrance().style}>
                  <Link
                    to={`/academy/programs/${p.id}`}
                    data-testid="program-item"
                    class="flex items-center gap-3 rounded-2xl border border-border p-4 transition-[color,background-color,border-color,transform] duration-150 ease-out hover:border-primary/50 active:scale-[0.99]"
                  >
                    <span class="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/12 text-accent">
                      <GraduationCap class="h-5 w-5" />
                    </span>
                    <div>
                      <p class="font-semibold">{p.name}</p>
                      <p class="text-xs text-muted-foreground">{p.kind}</p>
                    </div>
                  </Link>
                </li>
              );
            }}
          </For>
        </ul>
      </Show>
    </div>
  );
};

export const ProgramDetailView: Component<{ programId: string }> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const drizzle = useDrizzle();
  const { db: syncDb } = usePowerSyncContext();
  const { activeOrgId } = useActiveOrg();
  const program = useProgram(() => props.programId);
  const programCourses = useProgramCourses(() => props.programId);
  const orgCourses = useOrgCourses(activeOrgId);

  // LF-B: un programa inexistente / de otra org / sin sincronizar dejaba el
  // skeleton PARA SIEMPRE — resolución canónica loading/not-found/offline.
  const resolution = createEntityResolution({
    db: syncDb,
    hasEntity: () => Boolean(program.data),
    isPending: () => program.isLoading,
  });

  const titleFor = (courseId: string | null) =>
    (orgCourses.data ?? []).find((c) => c.id === courseId)?.title ?? courseId;

  const addCourse = (courseId: string) => {
    const db = drizzle();
    const org = activeOrgId();
    if (!db || !org || !courseId) {
      return;
    }
    void addProgramCourseMutation(db, {
      programId: props.programId,
      organizationId: org,
      courseId,
      position: programCourses.data?.length ?? 0,
    });
  };

  return (
    <div class="mx-auto max-w-3xl space-y-5 p-4 md:p-6">
      <Link to="/academy/programs" class="text-sm text-muted-foreground hover:text-foreground">
        ← {t(msg`Programs`)}
      </Link>
      <Show
        when={program.data}
        fallback={
          <Show
            when={resolution() !== "loading"}
            fallback={<Skeleton class="h-32 w-full rounded-2xl" />}
          >
            <div data-testid="program-not-found">
              <NotFoundState
                variant={resolution() === "offline" ? "offline" : "no-access"}
                title={t(msg`Program not available`)}
                description={
                  resolution() === "offline"
                    ? t(
                        msg`You're offline and this program hasn't synced to this device yet. Reconnect and try again.`,
                      )
                    : t(msg`Program not found in this academy.`)
                }
                primaryAction={{
                  label: t(msg`Back to programs`),
                  onClick: () => void navigate({ to: "/academy/programs" }),
                }}
              />
            </div>
          </Show>
        }
      >
        {(p) => (
          <>
            <input
              class={`${field} text-xl font-bold`}
              data-testid="program-detail-name"
              value={p().name ?? ""}
              onChange={(e) => {
                const db = drizzle();
                if (db) {
                  void updateProgramMutation(db, p().id, { name: e.currentTarget.value });
                }
              }}
            />
            <div class="space-y-2" data-testid="program-courses">
              <For each={programCourses.data ?? []}>
                {(pc) => (
                  <div
                    class="flex items-center justify-between rounded-xl border border-border p-3"
                    data-testid="program-course-row"
                  >
                    <span class="text-sm font-semibold">{titleFor(pc.course_id)}</span>
                    <button
                      type="button"
                      data-testid="program-course-remove"
                      onClick={() => {
                        const db = drizzle();
                        if (db) {
                          void removeProgramCourseMutation(db, pc.id);
                        }
                      }}
                      class="text-muted-foreground hover:text-danger"
                    >
                      <Trash2 class="h-4 w-4" />
                    </button>
                  </div>
                )}
              </For>
              <select
                class={field}
                data-testid="program-add-course"
                onChange={(e) => {
                  addCourse(e.currentTarget.value);
                  e.currentTarget.value = "";
                }}
              >
                <option value="">{t(msg`Add course to program…`)}</option>
                <For each={orgCourses.data ?? []}>
                  {(c) => <option value={c.id}>{c.title}</option>}
                </For>
              </select>
            </div>
          </>
        )}
      </Show>
    </div>
  );
};
